import { PAYMENT_STATUS_COLOR_ENUM, PAYMENT_STATUS_ENUM } from '@modules/private/payment/data/enum/payment.enum'
import { ITableCol } from '@data/interfaces/itable.interface'

/**
 * El `status` guardado del pago en el vocabulario corto, en mayúsculas, que la herramienta de ajuste ya
 * enseñaba en la evidencia de pagos iguales (APLICADO, PARCIAL, PENDIENTE); el gemelo de «Editar pago» es
 * la misma evidencia y lo repite para que Operaciones lea lo mismo en los dos sitios. `PAYMENT_STATUS_OPTIONS` es el del filtro de la tabla
 * («Integrado»…): unificarlos cambia textos que ya se ven en pantalla, y eso no entra en F-045.
 */
export const PAYMENT_STATUS_LABELS: Readonly<Record<string, string>> = { PENDING: 'PENDIENTE', PARTIAL: 'PARCIAL', APPLIED: 'APLICADO' }

export const PAYMENT_STATUS_OPTIONS = [
  { value: 'APPLIED', label: 'Integrado' },
  { value: 'PENDING', label: 'Pendiente' },
  { value: 'PARTIAL', label: 'Parcialmente integrado' },
  { value: 'CANCELLED', label: 'Cancelado' },
  { value: 'EXCESS', label: 'Excedente' },
]

/**
 * Lo que Operaciones y Contabilidad necesitan **del pago** (D-04): cuánto entró, cuánto de eso ya se
 * aplicó, cuánto sigue suelto, y de quién es para poder saltar a su expediente.
 *
 * Se retiraron tres columnas que ocupaban ancho sin decir nada: el `_id` en crudo, la CLABE de la
 * cuenta —es la misma en todas las filas, la arrendadora cobra en una— y quién capturó. No se
 * borraron: quedan en el selector de columnas para el día que hagan falta.
 */
export const PAYMENT_COLUMNS: ITableCol[] = [
  //* La fecha del CLIENTE, que es la que manda en todo Cobranza (D-08): un depósito del 30 de agosto
  //* capturado el 2 de septiembre es de agosto. Va primero porque es por donde se busca.
  { type: 'date', field: 'date', header: 'Fecha de pago', width: '9rem', showFilter: true, dataType: 'date' },
  { type: 'text', field: 'client.name', header: 'Cliente', width: '14rem', showFilter: true, dataType: 'link', linkField: 'client._id', rootLink: 'customer' },
  { type: 'text', field: 'trackingKey', header: 'Clave de rastreo', width: '15rem', showFilter: true },
  { type: 'number', field: 'amount', header: 'Monto', width: '10rem', showFilter: true, dataType: 'currency' },
  /**
   * Derivada: el listado manda `amount` y `pendingToApply`, no lo aplicado. Se calcula aquí en vez
   * de cambiar el endpoint porque es una resta, y porque el mismo dato ya viaja completo.
   *
   * Sin `showFilter`: el filtro es del servidor y va por `field`, así que filtrar por un campo que
   * no existe en el documento devolvería cero filas sin decir por qué.
   */
  { type: 'number', field: 'applied', header: 'Aplicado', width: '10rem', dataType: 'currency', compute: (p: any) => Math.max(0, (p?.amount ?? 0) - (p?.pendingToApply ?? 0)) },
  { type: 'number', field: 'pendingToApply', header: 'Sin aplicar', width: '10rem', showFilter: true, dataType: 'currency' },
  {
    type: 'enum',
    field: 'status',
    header: 'Estatus',
    width: '12rem',
    showFilter: true,
    dataType: 'enum',
    enum: PAYMENT_STATUS_ENUM,
    enumColor: PAYMENT_STATUS_COLOR_ENUM,
    options: PAYMENT_STATUS_OPTIONS,
  },

  //* Disponibles en el selector, fuera de la tabla por defecto.
  { type: 'text', field: 'account.clabe', header: 'Cuenta', showFilter: true, hidden: true },
  { type: 'text', field: 'user.names', header: 'Capturó', showFilter: true, hidden: true },
  { type: 'text', field: '_id', header: 'id del Pago', showFilter: false, hidden: true },
]
